import styled from "@emotion/styled";
import { FC, RefObject, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { ReactEditor, useSlate } from "slate-react";

import { EmailAt } from "@livechat/design-system-icons";
import {
  DesignToken,
  IPickerListItem,
  Icon,
  Picker,
  RadiusToken,
  ShadowToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { useClickOutside } from "hooks/useClickOutside";
import { IAgent, selectActiveAgents } from "store/entities/agents";
import { getCurrentAgentId } from "store/features/session";
import { withVar } from "themes/helpers";

interface IProps {
  onSelect: (agent: IAgent) => void;
}

export const MentionsList: FC<IProps> = ({ onSelect }) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLElement | Window | null>(null);

  const editor = useSlate();
  const agents = useSelector(selectActiveAgents);
  const loggedInAgentId = useSelector(getCurrentAgentId);

  const items = useMemo<IPickerListItem[]>(
    () =>
      agents.map(({ id, name, avatar, email }) => ({
        key: id,
        name: name + email,
        customElement: {
          listItemBody: (
            <AgentPickerListItem
              agentName={name}
              avatarUrl={avatar}
              isLoggedInAgent={id === loggedInAgentId}
              secondaryText={email}
            />
          ),
          selectedItemBody: <AgentPickerSelectedListItem agentName={name} avatarUrl={avatar} />,
        },
      })),
    [agents]
  );

  const handleSelect = (selectedItems: IPickerListItem[] | null): void => {
    if (selectedItems && selectedItems.length > 0) {
      const selectedAgent = agents.find(agent => agent.id === selectedItems[0].key);

      if (selectedAgent) {
        ReactEditor.focus(editor);
        editor.insertMention(selectedAgent);
        onSelect(selectedAgent);
      }
    }
  };

  const hideMentionsList = (): void => {
    editor.mentions.onLeave();
  };

  const handleClose = (event?: Event | undefined): void => {
    // on the Firefox browser "focusout" event is triggered when the picker is rendered causing losing focus on the input,
    // so we need to force focus it again, it is a temporary workaround until we find a better solution
    if (event && event.type === "focusout") (event.target as HTMLInputElement)?.focus();
  };

  useClickOutside({
    elementRef: [pickerRef, optionsRef as RefObject<HTMLElement>],
    onClickOutside: hideMentionsList,
  });

  useEffect(() => {
    const closeOnEscapeClick = (event: KeyboardEvent): void => {
      if (event.key === "Escape") hideMentionsList();
    };

    document.addEventListener("keydown", closeOnEscapeClick);

    return (): void => {
      document.removeEventListener("keydown", closeOnEscapeClick);
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <ListContainer ref={pickerRef} onKeyDown={handleKeyDown} data-testid="mention-picker">
      <Header>
        <Icon source={EmailAt} />
        <Text bold>Mention Agent</Text>
      </Header>
      <Picker
        isVisible
        options={items}
        onSelect={handleSelect}
        placeholder="Search…"
        data-testid="mention-picker-search-input"
        onClose={handleClose}
        maxListHeight={150}
        useDismissHookProps={{ enabled: false }}
        virtuosoProps={{ scrollerRef: ref => (optionsRef.current = ref) }}
      />
    </ListContainer>
  );
};

const ListContainer = styled.div`
  position: relative;
  width: 300px;
  padding: ${withVar(SpacingToken.Spacing3)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  box-shadow: ${withVar(ShadowToken.PopOver)};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing1)};
  margin-bottom: ${withVar(SpacingToken.Spacing3)};
`;
